import { Info } from 'lucide-react';
import styles from '../BusinessProfileMain.module.css';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../../../utils/auth';
import Auth from '../../../utils/auth';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Dynamic lists focused on a US-based platform
const countries = [
  { code: '', name: 'Select Country' },
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'MX', name: 'Mexico' },
];
const timezones = [
  { value: '', label: 'Select Time Zone' },
  { value: 'America/New_York', label: 'Eastern Time (US & Canada)' },
  { value: 'America/Chicago', label: 'Central Time (US & Canada)' },
  { value: 'America/Denver', label: 'Mountain Time (US & Canada)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (US & Canada)' },
  { value: 'America/Phoenix', label: 'Arizona' },
  { value: 'America/Anchorage', label: 'Alaska' },
  { value: 'Pacific/Honolulu', label: 'Hawaii' },
];
const languages = [
  { value: '', label: 'Select Language' },
  { value: 'en', label: 'English (United States)' },
  { value: 'es', label: 'Spanish (United States)' },
];

const BusinessPhysicalAddress = () => {
  const [fields, setFields] = useState({
    businessAddress: '',
    city: '',
    postal: '',
    state: '',
    country: '',
    timezone: '',
    platformLanguage: '',
    outboundCommunicationLanguage: '',
  });
  const [id, setId] = useState(null);
  const [loading, setLoading] = useState(false);
  const workspaceId = Auth.getWorkspaceId();
  const userId = Auth.getUser()?.id;

  useEffect(() => {
    // Fetch business general information on mount
    const fetchInfo = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API_URL}/business-general-information/business-general-information`, {
          params: { workspaceId, userId }
        });
        if (data && !data.error) {
          setId(data.id || null);
          setFields(prev => ({
            ...prev,
            businessAddress: data.businessAddress || '',
            city: data.city || '',
            postal: data.postal || '',
            state: data.state || '',
            country: data.country || '',
            timezone: data.timezone || '',
            platformLanguage: data.platformLanguage || '',
            outboundCommunicationLanguage: data.outboundCommunicationLanguage || '',
          }));
        } else {
          setId(null);
        }
      } catch (err) {
        setId(null);
      } finally {
        setLoading(false);
      }
    };
    if (workspaceId && userId) fetchInfo();
    // eslint-disable-next-line
  }, [workspaceId, userId]);

  // Handle input changes for controlled fields
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFields(prev => ({ ...prev, [name]: value }));
  };

  // Handle form submission for updating business address info
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let body;
      
      if (!id) {
        // If no ID, this is the first PUT - send all fields
        const { data: currentData } = await axios.get(`${API_URL}/business-general-information/business-general-information`, {
          params: { workspaceId, userId }
        });
        body = {
          ...(currentData && !currentData.error ? currentData : {}),
          workspaceId,
          userId,
          ...fields,
        };
      } else {
        // If ID exists, only send fields that have changed
        const changedFields = {};
        Object.keys(fields).forEach(key => {
          if (fields[key] !== '' && fields[key] !== null && fields[key] !== undefined) {
            changedFields[key] = fields[key];
          }
        });
        
        body = {
          id,
          workspaceId,
          userId,
          ...changedFields,
        };
      }

      const { data } = await axios.put(`${API_URL}/business-general-information/business-general-information`, body);
      if (data && !data.error) {
        toast.success('Address updated!');
        if (data.id) setId(data.id);
      } else {
        toast.error(data.error || 'Error updating address');
      }
    } catch (err) {
      toast.error('Error updating address');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.profileSection}>
      <form onSubmit={handleSubmit}>
        <div className={styles.sectionHeader}>
          <h2>Business Physical Address <Info className={styles.infoIcon} size={16} /></h2>
        </div>
        <div className={styles.formGroup}>
          <label>Street Address <Info className={styles.infoIcon} size={16} /></label>
          <input name="businessAddress" value={fields.businessAddress} onChange={handleChange} type="text" placeholder="Enter street address" className={styles.formControl} />
        </div>
        <div className={`${styles.formGroup} ${styles.twoCol}`}>
          <div>
            <label>City</label>
            <input name="city" value={fields.city} onChange={handleChange} type="text" placeholder="Enter city" className={styles.formControl} />
          </div>
          <div>
            <label>Postal/Zip Code</label>
            <input name="postal" value={fields.postal} onChange={handleChange} type="text" placeholder="Enter postal code" className={styles.formControl} />
          </div>
        </div>
        <div className={styles.formGroup}>
          <label>State / Province / Region</label>
          <input name="state" value={fields.state} onChange={handleChange} type="text" placeholder="Enter state or province" className={styles.formControl} />
        </div>
        <div className={styles.formGroup}>
          <label>Country</label>
          <select name="country" value={fields.country} onChange={handleChange} className={styles.formControl}>
            {countries.map(c => (
              <option key={c.code} value={c.code}>{c.name}</option>
            ))}
          </select>
        </div>
        <div className={styles.formGroup}>
          <label>Time Zone <Info className={styles.infoIcon} size={16} /></label>
          <select name="timezone" value={fields.timezone} onChange={handleChange} className={styles.formControl}>
            {timezones.map(tz => (
              <option key={tz.value} value={tz.value}>{tz.label}</option>
            ))}
          </select>
        </div>
        <div className={styles.formGroup}>
          <label>Platform Language <Info className={styles.infoIcon} size={16} /></label>
          <select name="platformLanguage" value={fields.platformLanguage} onChange={handleChange} className={styles.formControl}>
            {languages.map(l => (
              <option key={l.value} value={l.value}>{l.label}</option>
            ))}
          </select>
        </div>
        <div className={styles.formGroup}>
          <label>Outbound Communication Language for Custom Values <Info className={styles.infoIcon} size={16} /></label>
          <select name="outboundCommunicationLanguage" value={fields.outboundCommunicationLanguage} onChange={handleChange} className={styles.formControl}>
            {languages.map(l => (
              <option key={l.value} value={l.value}>{l.label}</option>
            ))}
          </select>
        </div>
        <div className={styles.updateSection}>
          <button className={`${styles.updateBtn} ${styles.primary}`} type="submit" disabled={loading}>{loading ? 'Saving...' : 'Update'}</button>
        </div>
      </form>
    </div>
  );
};

export default BusinessPhysicalAddress; 